import type { CSSProperties } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { COMPONENT_NAV, COMPONENT_CATEGORIES, type ComponentCategory } from './ui._layout.js';

/**
 * /components — overview index.
 *
 * Groups every per-component docs page into categories (Actions,
 * Inputs, Feedback, …) so the reader has anchor points instead of
 * a flat 37-card list. Renders inside the pathless `/ui/_layout`
 * (which provides the per-component side nav).
 *
 * root/osd#127 — browser audit 2026-09-22: "ui overview page is very
 * busy." The category grouping is the fix.
 */
export const Route = createFileRoute('/ui/_layout/')({
  component: ComponentsIndex,
});

const SECTION_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};
const LIST_STYLE: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '0.5rem',
};
const ITEM_STYLE: CSSProperties = {
  border: '1px solid var(--osd-color-border, #e4e4e7)',
  borderRadius: 'var(--osd-radius-md, 6px)',
  padding: '0.625rem 0.875rem',
  backgroundColor: 'var(--osd-color-surface-1)',
};
const LINK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.125rem',
  color: 'inherit',
  textDecoration: 'none',
};

// Bucket every component by its declared category. Categories
// without any entries are skipped (e.g. Pagination doesn't exist
// yet, so the Navigation section hides).
function bucketByCategory(): ReadonlyArray<{
  readonly category: ComponentCategory;
  readonly items: ReadonlyArray<(typeof COMPONENT_NAV)[number]>;
}> {
  const buckets = new Map<ComponentCategory, Array<(typeof COMPONENT_NAV)[number]>>();
  for (const item of COMPONENT_NAV) {
    const list = buckets.get(item.category) ?? [];
    list.push(item);
    buckets.set(item.category, list);
  }
  return COMPONENT_CATEGORIES.filter((c) => buckets.has(c)).map((category) => ({
    category,
    items: (buckets.get(category) ?? []).slice().sort((a, b) => a.label.localeCompare(b.label)),
  }));
}

function ComponentsIndex() {
  const sections = bucketByCategory();
  return (
    <Stack gap={5}>
      <header>
        <Heading level={2}>Overview</Heading>
        <Text muted>
          {COMPONENT_NAV.length} primitives grouped by purpose. Pick a component to see its
          live demo and usage caption.
        </Text>
      </header>
      {sections.map(({ category, items }) => (
        <section key={category} aria-labelledby={`cat-${category}`} style={SECTION_STYLE}>
          <Heading id={`cat-${category}`} level={3}>
            {category} <Text muted>({items.length})</Text>
          </Heading>
          <ul style={LIST_STYLE}>
            {items.map((item) => (
              <li key={item.slug} style={ITEM_STYLE}>
                <Link to={item.to} style={LINK_STYLE}>
                  <strong>{item.label}</strong>
                  <Text muted>{item.summary}</Text>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </Stack>
  );
}
